/**
 * The fisherman's bearer credential, held as module-level state rather than
 * inside the Pinia auth store.
 *
 * `src/api/client.ts` reads `sessionToken` synchronously on every request
 * (`getToken` in `@fcms/ui`'s API client is a plain function, called per
 * request so a refreshed token is never stale). Putting the token in the
 * Pinia store instead would mean `api/client.ts` importing `stores/auth.ts`
 * while `stores/auth.ts` imports `api/client.ts` to call the OTP endpoints —
 * a real circular dependency. This module breaks the cycle the same way
 * `@fcms/ui`'s own `useTheme`/`useLocale` are module singletons rather than
 * Pinia stores: the primitive that needs to be readable from anywhere (a
 * fetch wrapper has no component context) is not one.
 */
import { computed, ref } from 'vue'

const STORAGE_KEY = 'fcms.portal.session'

interface StoredSession {
  token: string
  expiresAt: string
  fishermanId: string
  displayName?: string
}

function readStored(): StoredSession | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredSession>
    if (!parsed.token || !parsed.expiresAt || !parsed.fishermanId) return null
    return parsed as StoredSession
  } catch {
    return null
  }
}

function writeStored(value: StoredSession | null): void {
  if (typeof localStorage === 'undefined') return
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Safari private mode throws on write. Losing persistence is tolerable —
    // the session still works for the rest of this tab's lifetime.
  }
}

const stored = readStored()

/** `null` when signed out. Read synchronously by the API client. */
export const sessionToken = ref<string | null>(stored?.token ?? null)
export const sessionExpiresAt = ref<string | null>(stored?.expiresAt ?? null)
export const sessionFishermanId = ref<string | null>(stored?.fishermanId ?? null)
export const sessionDisplayName = ref<string | null>(stored?.displayName ?? null)

/** True once we have a token that has not passed its own expiry locally. */
export const isSessionActive = computed(() => {
  if (!sessionToken.value || !sessionExpiresAt.value) return false
  return Date.parse(sessionExpiresAt.value) > Date.now()
})

export function setSession(input: {
  token: string
  expiresAt: string
  fishermanId: string
  displayName?: string
}): void {
  sessionToken.value = input.token
  sessionExpiresAt.value = input.expiresAt
  sessionFishermanId.value = input.fishermanId
  sessionDisplayName.value = input.displayName ?? null
  writeStored({
    token: input.token,
    expiresAt: input.expiresAt,
    fishermanId: input.fishermanId,
    displayName: input.displayName,
  })
}

export function clearSession(): void {
  sessionToken.value = null
  sessionExpiresAt.value = null
  sessionFishermanId.value = null
  sessionDisplayName.value = null
  writeStored(null)
}

// Another tab logged out (or in). Fishermen on a shared family device open
// the portal in more than one tab more often than staff do.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return
    if (!event.newValue) {
      sessionToken.value = null
      sessionExpiresAt.value = null
      sessionFishermanId.value = null
      sessionDisplayName.value = null
      return
    }
    try {
      const parsed = JSON.parse(event.newValue) as StoredSession
      sessionToken.value = parsed.token
      sessionExpiresAt.value = parsed.expiresAt
      sessionFishermanId.value = parsed.fishermanId
      sessionDisplayName.value = parsed.displayName ?? null
    } catch {
      /* ignore a malformed write from another tab */
    }
  })
}
